import { useState } from 'react';
import { Users, Edit, Trash2, Check, X, Plus, Settings, Shield, Coffee, Monitor, CreditCard, Phone, RefreshCw, Loader2, ChefHat, Sparkles, ShieldCheck, Droplets, CupSoda } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { api } from '@/utils/apiClient';

// Role definitions with descriptions and permissions
// Note: Manager and Supervisor roles have been combined into 'Mgr-Sup' as of June 2026
const ROLES = {
  owner: {
    label: 'Owner',
    description: 'Full access to all features and settings. Logs in at /admin with email & password.',
    color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    icon: Shield,
    permissions: ['all'],
    loginMethod: 'email_password',
    loginLocation: '/admin'
  },
  'mgr-sup': {
    label: 'Mgr-Sup',
    description: 'Combined Manager/Supervisor role. POS access, shift approvals, staff oversight. Cannot see financials/costs. Logs in at /pos or /staff/clock with PIN.',
    color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    icon: Shield,
    permissions: ['supervision', 'overrides', 'staff_management', 'pos_terminal'],
    loginMethod: 'pin',
    loginLocation: '/pos'
  },
  cashier: {
    label: 'Cashier',
    description: 'POS terminal access for all order types. Logs in at /pos with 6-digit PIN.',
    color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    icon: CreditCard,
    permissions: ['pos_terminal', 'all_orders', 'payments', 'cash_drawer'],
    loginMethod: 'pin',
    loginLocation: '/pos'
  },
  server: {
    label: 'Server',
    description: 'Handheld device access for dine-in orders. Logs in at /server with 6-digit PIN.',
    color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    icon: Coffee,
    permissions: ['server_handheld', 'dine_in_orders'],
    loginMethod: 'pin',
    loginLocation: '/server'
  },
  display: {
    label: 'Display',
    description: 'Counter display access for order queue management. Logs in at /display with 6-digit PIN.',
    color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    icon: Monitor,
    permissions: ['order_queue', 'approve_kiosk_orders', 'mark_complete', 'update_times'],
    loginMethod: 'pin',
    loginLocation: '/display'
  },
  'main-chef': {
    label: 'Main Chef',
    description: 'Head chef - Kitchen operations leader. Logs in at /staff/clock with PIN.',
    color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    icon: ChefHat,
    permissions: ['kitchen', 'time_clock'],
    loginMethod: 'pin',
    loginLocation: '/staff/clock'
  },
  'chef': {
    label: '2nd Chef',
    description: 'Secondary chef - Kitchen support. Logs in at /staff/clock with PIN.',
    color: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
    icon: ChefHat,
    permissions: ['kitchen', 'time_clock'],
    loginMethod: 'pin',
    loginLocation: '/staff/clock'
  },
  'dishwasher': {
    label: 'Dishwasher',
    description: 'Dishwashing and cleaning duties. Logs in at /staff/clock with PIN.',
    color: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
    icon: Droplets,
    permissions: ['time_clock'],
    loginMethod: 'pin',
    loginLocation: '/staff/clock'
  },
  'juicer': {
    label: 'Juicer',
    description: 'Fresh juice preparation. Logs in at /staff/clock with PIN.',
    color: 'bg-lime-100 text-lime-700 dark:bg-lime-900/30 dark:text-lime-400',
    icon: Sparkles,
    permissions: ['time_clock'],
    loginMethod: 'pin',
    loginLocation: '/staff/clock'
  },
  'security': {
    label: 'Security',
    description: 'Security and safety. Logs in at /staff/clock with PIN.',
    color: 'bg-slate-100 text-slate-700 dark:bg-slate-900/30 dark:text-slate-400',
    icon: ShieldCheck,
    permissions: ['time_clock'],
    loginMethod: 'pin',
    loginLocation: '/staff/clock'
  },
  'gatherer': {
    label: 'Gatherer',
    description: 'Order gathering and preparation support. Logs in at /staff/clock with PIN.',
    color: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400',
    icon: Users,
    permissions: ['time_clock'],
    loginMethod: 'pin',
    loginLocation: '/staff/clock'
  },
  'barista': {
    label: 'Barista',
    description: 'Coffee and beverage preparation specialist. Logs in at /staff/clock with PIN.',
    color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    icon: CupSoda,
    permissions: ['barista_display', 'time_clock'],
    loginMethod: 'pin',
    loginLocation: '/staff/clock'
  }
};

export default function AdminUsersTab({ users, onRefresh, currentAdmin }) {
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', role: '' });
  const [changingPassword, setChangingPassword] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to permanently delete this admin user? This action cannot be undone.')) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success('Admin user deleted');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete admin');
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user.id);
    setEditForm({
      name: user.name,
      email: user.email,
      role: user.role,
      phone: user.phone || ''
    });
  };

  const handleSaveEdit = async (userId) => {
    try {
      await api.put(`/admin/users/${userId}`, editForm);
      toast.success('User updated successfully');
      setEditingUser(null);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleChangePassword = async (userId) => {
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    try {
      await api.put(`/admin/users/${userId}/password`, { new_password: newPassword });
      toast.success('Password changed successfully');
      setChangingPassword(null);
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    }
  };

  const canManage = currentAdmin.role === 'owner';

  // Phone editing state and handler
  const [editingPhone, setEditingPhone] = useState(null);
  const [phoneInput, setPhoneInput] = useState('');

  const handleUpdatePhone = async (userId) => {
    try {
      await api.put(`/admin/users/${userId}/phone`, { phone: phoneInput });
      toast.success('Phone number updated');
      setEditingPhone(null);
      setPhoneInput('');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update phone');
    }
  };

  // Migration handler for legacy roles
  const [migratingRoles, setMigratingRoles] = useState(false);
  const handleMigrateLegacyRoles = async () => {
    if (!window.confirm('This will convert all Manager and Supervisor accounts to the unified Mgr-Sup role. Continue?')) {
      return;
    }
    setMigratingRoles(true);
    try {
      const res = await api.post('/admin/migrate-legacy-roles');
      toast.success(res.data.message);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Migration failed');
    } finally {
      setMigratingRoles(false);
    }
  };

  // Check if there are legacy roles to migrate
  const hasLegacyRoles = users.some(u => u.role === 'manager' || u.role === 'supervisor');

  return (
    <div data-testid="admin-users-tab">
      <div className="flex justify-between items-center mb-8">
        <h1 className="font-heading text-3xl text-foreground">Admin Users</h1>
        <div className="flex gap-2">
          {canManage && hasLegacyRoles && (
            <Button 
              onClick={handleMigrateLegacyRoles} 
              variant="outline" 
              className="text-amber-600 border-amber-300 hover:bg-amber-50 dark:text-amber-400 dark:border-amber-700 dark:hover:bg-amber-900/30"
              disabled={migratingRoles}
              data-testid="migrate-legacy-roles-btn"
            >
              {migratingRoles ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Migrate Legacy Roles
            </Button>
          )}
          {canManage && (
            <Button onClick={() => setShowAddModal(true)} className="rounded-full bg-primary hover:bg-primary/90">
              <Plus className="w-4 h-4 mr-2" />
              Add Admin
            </Button>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {users.map(user => (
          <div key={user.id} className="bg-card rounded-2xl p-6 shadow-sm border border-border" data-testid={`admin-user-${user.id}`}>
            {editingUser === user.id ? (
              <div className="space-y-4">
                <div>
                  <Label>Name</Label>
                  <Input
                    value={editForm.name}
                    onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                    data-testid="edit-admin-name"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                    data-testid="edit-admin-email"
                  />
                </div>
                <div>
                  <Label>Phone (for SMS alerts)</Label>
                  <Input
                    type="tel"
                    value={editForm.phone || ''}
                    onChange={(e) => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="+679 1234567"
                    data-testid="edit-admin-phone"
                  />
                </div>
                <div>
                  <Label>Role</Label>
                  <Select value={editForm.role} onValueChange={(value) => setEditForm(prev => ({ ...prev, role: value }))}>
                    <SelectTrigger data-testid="edit-admin-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="owner">
                        <div className="flex items-center gap-2">
                          <Shield className="w-4 h-4 text-amber-600" />
                          <span>Owner - Full Access</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="mgr-sup">
                        <div className="flex items-center gap-2">
                          <Shield className="w-4 h-4 text-orange-600" />
                          <span>Mgr-Sup - Manager/Supervisor</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="cashier">
                        <div className="flex items-center gap-2">
                          <CreditCard className="w-4 h-4 text-blue-600" />
                          <span>Cashier - POS Only</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="server">
                        <div className="flex items-center gap-2">
                          <Coffee className="w-4 h-4 text-green-600" />
                          <span>Server - Handheld Only</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="display">
                        <div className="flex items-center gap-2">
                          <Monitor className="w-4 h-4 text-purple-600" />
                          <span>Display - Queue Only</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="main-chef">
                        <div className="flex items-center gap-2">
                          <ChefHat className="w-4 h-4 text-red-600" />
                          <span>Main Chef - Kitchen Leader</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="chef">
                        <div className="flex items-center gap-2">
                          <ChefHat className="w-4 h-4 text-rose-600" />
                          <span>2nd Chef - Kitchen Support</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="dishwasher">
                        <div className="flex items-center gap-2">
                          <Droplets className="w-4 h-4 text-cyan-600" />
                          <span>Dishwasher</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="juicer">
                        <div className="flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-lime-600" />
                          <span>Juicer</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="security">
                        <div className="flex items-center gap-2">
                          <ShieldCheck className="w-4 h-4 text-slate-600" />
                          <span>Security</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="gatherer">
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-teal-600" />
                          <span>Gatherer</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="barista">
                        <div className="flex items-center gap-2">
                          <CupSoda className="w-4 h-4 text-amber-600" />
                          <span>Barista</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => handleSaveEdit(user.id)} className="bg-primary" data-testid="save-admin-edit">
                    <Check className="w-4 h-4 mr-1" /> Save
                  </Button>
                  <Button variant="outline" onClick={() => setEditingUser(null)}>
                    <X className="w-4 h-4 mr-1" /> Cancel
                  </Button>
                </div>
              </div>
            ) : changingPassword === user.id ? (
              <div className="space-y-4">
                <h3 className="font-medium text-foreground">
                  {user.role === 'owner' ? `Change Password/PIN for ${user.name}` : `Change PIN for ${user.name}`}
                </h3>
                {user.role === 'owner' ? (
                  <>
                    <div>
                      <Label>New Dashboard Password</Label>
                      <Input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Enter new password (min 6 characters)"
                        data-testid="new-password-input"
                      />
                    </div>
                    <div>
                      <Label>Confirm Password</Label>
                      <Input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm new password"
                        data-testid="confirm-password-input"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <Label>New 6-Digit PIN</Label>
                      <Input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={6}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="Enter new 6-digit PIN"
                        className="font-mono text-xl tracking-widest text-center"
                        data-testid="new-pin-input"
                      />
                    </div>
                    <div>
                      <Label>Confirm PIN</Label>
                      <Input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={6}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="Confirm new PIN"
                        className="font-mono text-xl tracking-widest text-center"
                        data-testid="confirm-pin-input"
                      />
                    </div>
                  </>
                )}
                <div className="flex gap-2">
                  <Button onClick={() => handleChangePassword(user.id)} className="bg-primary" data-testid="save-password-btn">
                    <Check className="w-4 h-4 mr-1" /> {user.role === 'owner' ? 'Change Password' : 'Change PIN'}
                  </Button>
                  <Button variant="outline" onClick={() => { setChangingPassword(null); setNewPassword(''); setConfirmPassword(''); }}>
                    <X className="w-4 h-4 mr-1" /> Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <Users className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground">{user.name}</h3>
                    <p className="text-sm text-muted-foreground">
                      {user.email || <span className="italic">No email</span>}
                    </p>
                    {user.phone && (
                      <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                        <span className="text-green-600 dark:text-green-400">SMS:</span> {user.phone}
                      </p>
                    )}
                  </div>
                </div>
                
                {/* PIN Display for admin reference */}
                {user.pin && (
                  <div className="mb-4 p-2 bg-muted/50 rounded-lg border border-border">
                    <p className="text-xs text-muted-foreground mb-1">POS Login PIN</p>
                    <p className="font-mono text-lg tracking-widest font-semibold text-foreground" data-testid={`pin-display-${user.id}`}>
                      {user.pin}
                    </p>
                  </div>
                )}
                
                <div className="flex justify-between items-center mb-4">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium capitalize flex items-center gap-1.5 ${
                    ROLES[user.role]?.color || 'bg-muted text-foreground'
                  }`}>
                    {ROLES[user.role]?.icon && (() => {
                      const IconComponent = ROLES[user.role].icon;
                      return <IconComponent className="w-3.5 h-3.5" />;
                    })()}
                    {ROLES[user.role]?.label || user.role}
                  </span>
                  <span className={`text-xs ${user.is_active ? 'text-green-600' : 'text-red-500'}`}>
                    {user.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                {ROLES[user.role]?.description && (
                  <p className="text-xs text-muted-foreground mb-4">{ROLES[user.role].description}</p>
                )}
                {canManage && (
                  <div className="flex gap-2 flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => handleEdit(user)} data-testid={`edit-admin-${user.id}`}>
                      <Edit className="w-3 h-3 mr-1" /> Edit
                    </Button>
                    {['owner', 'mgr-sup', 'manager', 'supervisor'].includes(user.role) && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => { setEditingPhone(user.id); setPhoneInput(user.phone || ''); }}
                        className={user.phone ? 'text-green-600 border-green-300 dark:text-green-400 dark:border-green-800' : 'text-amber-600 border-amber-300 dark:text-amber-400 dark:border-amber-800'}
                        data-testid={`sms-${user.id}`}
                      >
                        <Phone className="w-3 h-3 mr-1" /> {user.phone ? 'SMS' : 'Add Phone'}
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => setChangingPassword(user.id)} data-testid={`change-password-${user.id}`}>
                      <Settings className="w-3 h-3 mr-1" /> {user.role === 'owner' ? 'Password' : 'PIN'}
                    </Button>
                    {user.id !== currentAdmin.id && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-destructive hover:bg-destructive hover:text-white"
                        onClick={() => handleDelete(user.id)}
                        data-testid={`delete-admin-${user.id}`}
                      >
                        <Trash2 className="w-3 h-3 mr-1" /> Delete
                      </Button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        ))}
      </div>

      {/* Add Admin Modal */}
      <AdminUserModal 
        isOpen={showAddModal} 
        onClose={() => setShowAddModal(false)} 
        onSave={onRefresh} 
      />

      {/* Phone Number Edit Modal */}
      <Dialog open={!!editingPhone} onOpenChange={(open) => { if (!open) setEditingPhone(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-foreground">Update Phone Number</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Add a phone number to receive SMS alerts for late clock-ins and security warnings.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-foreground">Phone Number</Label>
              <Input
                type="tel"
                value={phoneInput}
                onChange={(e) => setPhoneInput(e.target.value)}
                placeholder="+679 1234567"
                className="mt-1"
                data-testid="phone-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Include country code (e.g., +679 for Fiji). Leave blank to disable SMS alerts.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditingPhone(null)}>Cancel</Button>
              <Button onClick={() => handleUpdatePhone(editingPhone)} data-testid="save-phone-btn">
                Save Phone
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Admin User Modal
function AdminUserModal({ isOpen, onClose, onSave }) {
  const [formData, setFormData] = useState({
    name: '', email: '', password: '', pin: '', phone: '', role: 'cashier'
  });
  const [loading, setLoading] = useState(false);

  const isOwnerRole = formData.role === 'owner';
  const isManagerRole = ['mgr-sup', 'owner'].includes(formData.role);
  const isPinRole = ['cashier', 'server', 'display', 'mgr-sup'].includes(formData.role);
  
  // Email is required for owner and manager roles
  const emailRequired = isManagerRole;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate PIN for ALL roles (required)
    if (!/^\d{6}$/.test(formData.pin)) {
      toast.error('PIN must be exactly 6 digits');
      return;
    }
    
    // Validate email for owner role
    if (isOwnerRole && !formData.email) {
      toast.error('Email is required for owner accounts');
      return;
    }
    
    // Validate password for owner role
    if (isOwnerRole && formData.password.length < 6) {
      toast.error('Password must be at least 6 characters for owner accounts');
      return;
    }
    
    setLoading(true);

    try {
      // Build request payload
      const payload = {
        name: formData.name,
        role: formData.role,
        pin: formData.pin,
        // For staff: password is the PIN, for manager/owner: use actual password
        password: isManagerRole ? formData.password : formData.pin
      };
      
      // Only include email if provided
      if (formData.email) {
        payload.email = formData.email;
      }
      
      // Include phone if provided (for SMS alerts)
      if (formData.phone) {
        payload.phone = formData.phone;
      }

      const res = await api.post('/admin/users', payload);

      toast.success(`${ROLES[formData.role]?.label || 'User'} created successfully!`);
      toast.info(`PIN: ${formData.pin} — They can log into ${ROLES[formData.role]?.loginLocation || '/pos'} with this PIN`);
      
      onSave();
      onClose();
      setFormData({ name: '', email: '', password: '', pin: '', phone: '', role: 'cashier' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    }
    setLoading(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Add Staff Member</DialogTitle>
          <DialogDescription>Create a new account for cafe staff or owner.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label>Name *</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="John Doe"
              required
              data-testid="add-user-name"
            />
          </div>
          
          {/* Role Selector - moved before other fields */}
          <div className="space-y-2">
            <Label>Role *</Label>
            <Select 
              value={formData.role} 
              onValueChange={(value) => setFormData(prev => ({ ...prev, role: value, password: '' }))}
              data-testid="add-user-role-select"
            >
              <SelectTrigger data-testid="add-user-role-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cashier">
                  <div className="flex flex-col">
                    <div className="flex items-center gap-2">
                      <CreditCard className="w-4 h-4 text-blue-600" />
                      <span className="font-medium">Cashier</span>
                    </div>
                    <span className="text-xs text-muted-foreground ml-6">POS terminal • Login with PIN</span>
                  </div>
                </SelectItem>
                <SelectItem value="server">
                  <div className="flex flex-col">
                    <div className="flex items-center gap-2">
                      <Coffee className="w-4 h-4 text-green-600" />
                      <span className="font-medium">Server</span>
                    </div>
                    <span className="text-xs text-muted-foreground ml-6">Server tablet • Login with PIN</span>
                  </div>
                </SelectItem>
                <SelectItem value="display">
                  <div className="flex flex-col">
                    <div className="flex items-center gap-2">
                      <Monitor className="w-4 h-4 text-purple-600" />
                      <span className="font-medium">Display</span>
                    </div>
                    <span className="text-xs text-muted-foreground ml-6">Order queue display • Login with PIN</span>
                  </div>
                </SelectItem>
                <SelectItem value="owner">
                  <div className="flex flex-col">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-amber-600" />
                      <span className="font-medium">Owner</span>
                    </div>
                    <span className="text-xs text-muted-foreground ml-6">Full admin access + POS • Login with email/password & PIN</span>
                  </div>
                </SelectItem>
                <SelectItem value="mgr-sup">
                  <div className="flex flex-col">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-orange-600" />
                      <span className="font-medium">Mgr-Sup</span>
                    </div>
                    <span className="text-xs text-muted-foreground ml-6">Combined Manager/Supervisor • POS + shift approvals • Login with PIN</span>
                  </div>
                </SelectItem>
                <SelectItem value="main-chef">
                  <div className="flex flex-col">
                    <div className="flex items-center gap-2">
                      <ChefHat className="w-4 h-4 text-red-600" />
                      <span className="font-medium">Main Chef</span>
                    </div>
                    <span className="text-xs text-muted-foreground ml-6">Head chef - Kitchen leader • Login with PIN</span>
                  </div>
                </SelectItem>
                <SelectItem value="chef">
                  <div className="flex flex-col">
                    <div className="flex items-center gap-2">
                      <ChefHat className="w-4 h-4 text-rose-600" />
                      <span className="font-medium">2nd Chef</span>
                    </div>
                    <span className="text-xs text-muted-foreground ml-6">Secondary chef - Kitchen support • Login with PIN</span>
                  </div>
                </SelectItem>
                <SelectItem value="dishwasher">
                  <div className="flex flex-col">
                    <div className="flex items-center gap-2">
                      <Droplets className="w-4 h-4 text-cyan-600" />
                      <span className="font-medium">Dishwasher</span>
                    </div>
                    <span className="text-xs text-muted-foreground ml-6">Dishwashing and cleaning • Login with PIN</span>
                  </div>
                </SelectItem>
                <SelectItem value="juicer">
                  <div className="flex flex-col">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-lime-600" />
                      <span className="font-medium">Juicer</span>
                    </div>
                    <span className="text-xs text-muted-foreground ml-6">Fresh juice preparation • Login with PIN</span>
                  </div>
                </SelectItem>
                <SelectItem value="security">
                  <div className="flex flex-col">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4 text-slate-600" />
                      <span className="font-medium">Security</span>
                    </div>
                    <span className="text-xs text-muted-foreground ml-6">Security and safety • Login with PIN</span>
                  </div>
                </SelectItem>
                <SelectItem value="gatherer">
                  <div className="flex flex-col">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-teal-600" />
                      <span className="font-medium">Gatherer</span>
                    </div>
                    <span className="text-xs text-muted-foreground ml-6">Order gathering support • Login with PIN</span>
                  </div>
                </SelectItem>
                <SelectItem value="barista">
                  <div className="flex flex-col">
                    <div className="flex items-center gap-2">
                      <CupSoda className="w-4 h-4 text-amber-600" />
                      <span className="font-medium">Barista</span>
                    </div>
                    <span className="text-xs text-muted-foreground ml-6">Coffee and beverage specialist • Login with PIN</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Email - Optional for staff, required for owner */}
          <div className="space-y-2">
            <Label>
              Email {emailRequired ? '*' : <span className="text-muted-foreground text-xs">(optional)</span>}
            </Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              placeholder={emailRequired ? "owner@bulaccinocafe.com" : "staff@example.com (optional)"}
              required={emailRequired}
              data-testid="add-user-email"
            />
            {!emailRequired && (
              <p className="text-xs text-muted-foreground">
                Email is optional for {formData.role} accounts. They&apos;ll log in using their PIN.
              </p>
            )}
          </div>
          
          {/* Password field - for owner and manager roles */}
          {isManagerRole && (
            <div className="space-y-2">
              <Label>Dashboard Password *</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                placeholder="Minimum 6 characters"
                minLength={6}
                required
                data-testid="add-user-password"
              />
              <p className="text-xs text-muted-foreground">
                Used to log into /admin dashboard with email
              </p>
            </div>
          )}
          
          {/* PIN field - REQUIRED for ALL roles */}
          <div className="space-y-2">
            <Label>6-Digit PIN * <span className="text-xs text-muted-foreground">(for POS/device login)</span></Label>
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              minLength={6}
              value={formData.pin}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                setFormData(prev => ({ ...prev, pin: value }));
              }}
              placeholder="Enter 6 digit PIN (e.g., 123456)"
              required
              className="font-mono text-2xl tracking-widest text-center"
              data-testid="add-user-pin"
            />
            <p className="text-xs text-muted-foreground">
              {isOwnerRole 
                ? 'Owners can also log into /pos with this PIN'
                : `This PIN will be used to log into ${ROLES[formData.role]?.loginLocation}`
              }
            </p>
          </div>
          
          {/* Phone field for SMS alerts - for mgr-sup/owner */}
          {['owner', 'mgr-sup'].includes(formData.role) && (
            <div className="space-y-2">
              <Label>
                Phone Number <span className="text-xs text-muted-foreground">(for SMS alerts)</span>
              </Label>
              <Input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+679 1234567"
                data-testid="add-user-phone"
              />
              <p className="text-xs text-muted-foreground">
                Receive SMS alerts for late clock-ins and security warnings
              </p>
            </div>
          )}

          {/* Role permissions info */}
          {formData.role && ROLES[formData.role] && (
            <div className={`p-3 rounded-lg ${ROLES[formData.role].color}`}>
              <p className="text-sm font-medium flex items-center gap-2">
                {ROLES[formData.role].icon && (() => {
                  const IconComponent = ROLES[formData.role].icon;
                  return <IconComponent className="w-4 h-4" />;
                })()}
                {ROLES[formData.role].label}
              </p>
              <p className="text-xs mt-1 opacity-80">{ROLES[formData.role].description}</p>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1 rounded-full">
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="flex-1 rounded-full bg-primary hover:bg-primary/90" data-testid="submit-add-user">
              {loading ? 'Creating...' : `Create ${ROLES[formData.role]?.label || 'User'}`}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
